/**
 * Agency Portal shell — Slice 4 (§2.2–§2.3).
 *
 * One view, seven sections. Replaces the five separate agency entries
 * in the account dropdown + the AgencyTabs row. Layout:
 *
 *   Desktop (≥ 1024 px): sticky left rail (200 px) + content column.
 *   Tablet / mobile:     horizontally scrollable chip row under the H1.
 *
 * Section state lives on the URL as `?view=agency&section=…`. Legacy
 * `?view=clients` etc. aliases are preserved by the ViewRouter and
 * surfaced as the matching section here.
 *
 * The portal is account-level. The ViewRouter clears the active site
 * when it mounts the portal, so merchant site tabs are hidden while
 * inside; the portal's own tab-less, chip-based nav takes over.
 */

import { Briefcase } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAgency } from '../../app/agency-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getAgencyOverview, type AgencyOverviewWire } from '../../api/agency-overview.js';
import { SiteCtx } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { ClientsList } from '../clients/ClientsList.js';
import { AgencyReports } from '../agency-reports/AgencyReports.js';
import { AgencyReferral } from '../agency-referral/AgencyReferral.js';
import { AgencyWidget } from '../agency-widget/AgencyWidget.js';
import { WhiteLabel } from '../white-label/WhiteLabel.js';
import { PortalLeads } from './PortalLeads.js';
import { PortalOverview } from './PortalOverview.js';
import {
  DEFAULT_SECTION,
  PORTAL_SECTIONS,
  SECTION_DESCRIPTIONS,
  SECTION_LABELS,
  parseSection,
  type PortalSection,
} from './portal-sections.js';
import { PortalShellProvider } from './portal-shell-context.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

function readUrlSection(): PortalSection {
  if (typeof window === 'undefined') return DEFAULT_SECTION;
  try {
    return parseSection(new URL(window.location.href).searchParams.get('section'));
  } catch {
    return DEFAULT_SECTION;
  }
}

function writeUrlSection(section: PortalSection): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    // Keep the canonical view id so a refresh resolves back to the
    // portal (see `RESTORABLE_VIEWS`). Legacy aliases (`clients`,
    // `agencyReports`, …) are rewritten by the view router.
    url.searchParams.set('view', 'agencyPortal');
    url.searchParams.set('section', section);
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* enhancement only */
  }
}

export function AgencyPortal(): JSX.Element {
  const C = useC();
  const { features, acting } = useAgency();
  const http = useOptionalHttp();
  // Read SiteCtx optionally so unit tests that render the portal without
  // a full `<SiteProvider>` keep working. Production always has it.
  const sites = useContext(SiteCtx);
  const [section, setSection] = useState<PortalSection>(() => readUrlSection());
  const [overview, setOverview] = useState<AgencyOverviewWire | null>(null);

  // Fetch the overview so the rail can show per-item counts. One
  // request serves the rail + the Overview page via the shared
  // network cache; polling isn't needed here.
  useEffect(() => {
    if (http === null) return;
    if (features === null || features.agency !== true) return;
    if (acting !== null) return;
    let cancelled = false;
    void (async () => {
      const r = await getAgencyOverview(http);
      if (!cancelled && r.kind === 'ok') setOverview(r.state);
    })();
    return () => {
      cancelled = true;
    };
  }, [http, features, acting]);

  const goTo = useCallback((s: PortalSection) => {
    setSection(s);
    writeUrlSection(s);
  }, []);

  // Keep the URL in sync so a refresh preserves the open section.
  useEffect(() => {
    writeUrlSection(section);
  }, [section]);

  // Spec §2.1 — the portal is account-level. Clear the active site on
  // mount so the TopBar hides merchant site tabs (`inSiteContext ===
  // false`) regardless of whether the user arrived via the TopBar
  // account menu (which already clears it) or via a direct URL /
  // refresh / history navigation (which previously left the merchant
  // tabs visible under the portal rail — v4 visual-QA finding).
  useEffect(() => {
    if (sites !== null && sites.activeSite !== null) {
      sites.setActiveSite(null);
    }
  }, [sites]);

  // Honour pushState / history popstate (older agency links use
  // `?view=clients` etc., which the ViewRouter rewrites, but a user
  // browsing back/forward should land on the right section).
  useEffect(() => {
    const onPop = (): void => setSection(readUrlSection());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Off-states take over the whole portal — the server already refuses
  // the surrounding routes but the UX surface must be honest.
  //
  // UX fix 2026-10-11 — the acting check runs BEFORE the features
  // check. When acting, `features` reflects the CLIENT account (where
  // the agency feature is off by construction); surfacing "Agency
  // features are not enabled" there is wrong and confusing. The
  // acting user always lands on the "Return to your agency" message.
  if (acting !== null) {
    return (
      <Shell C={C} section={section} goTo={goTo}>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.muted,
            padding: 24,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          You are acting inside a client. Return to your agency to use the
          Agency Portal.
        </div>
      </Shell>
    );
  }
  if (features !== null && features.agency !== true) {
    return (
      <Shell C={C} section={section} goTo={goTo}>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.muted,
            padding: 24,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          Agency features are not enabled on this account. Contact Sill if you
          think you need them.
        </div>
      </Shell>
    );
  }

  return (
    <Shell C={C} section={section} goTo={goTo} overview={overview}>
      {section === 'overview' ? <PortalOverview goTo={goTo} /> : null}
      {section === 'clients' ? <ClientsList /> : null}
      {section === 'reports' ? <AgencyReports /> : null}
      {section === 'leads' ? <PortalLeads goTo={goTo} /> : null}
      {section === 'referral' ? <AgencyReferral /> : null}
      {section === 'widget' ? <AgencyWidget /> : null}
      {section === 'white-label' ? <WhiteLabel /> : null}
    </Shell>
  );
}

interface ShellProps {
  readonly C: ReturnType<typeof useC>;
  readonly section: PortalSection;
  readonly goTo: (s: PortalSection) => void;
  readonly overview?: AgencyOverviewWire | null;
  readonly children: React.ReactNode;
}

function Shell({ C, section, goTo, overview = null, children }: ShellProps): JSX.Element {
  const auth = useOptionalAuth();
  const authed_user = auth?.state.kind === 'authenticated' ? auth.state.user : null;
  const agencyName =
    authed_user?.home_account_name !== undefined && authed_user.home_account_name.length > 0
      ? authed_user.home_account_name
      : 'Your agency';
  const sectionLabel = SECTION_LABELS[section];
  const sectionDesc = SECTION_DESCRIPTIONS[section];

  return (
    <div data-testid="agency-portal" className="w-full">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        {/* Mobile / tablet chip row (< lg). */}
        <div className="lg:hidden">
          <ChipRow section={section} goTo={goTo} C={C} />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-6 py-6">
          {/* Desktop left rail. */}
          <aside
            className="hidden lg:block"
            style={{
              flex: '0 0 200px',
              position: 'sticky',
              top: 72,
              alignSelf: 'flex-start',
            }}
            aria-label="Agency Portal"
          >
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                letterSpacing: 2,
                color: C.muted,
                textTransform: 'uppercase',
              }}
            >
              <Briefcase size={11} style={{ display: 'inline-block', verticalAlign: '-1px', marginRight: 6 }} />
              AGENCY PORTAL
            </div>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                marginTop: 4,
                marginBottom: 12,
                fontWeight: 500,
              }}
            >
              {agencyName}
            </div>
            <nav aria-label="Agency sections" className="space-y-0.5">
              {PORTAL_SECTIONS.map((s) => (
                <RailItem
                  key={s}
                  C={C}
                  active={s === section}
                  label={SECTION_LABELS[s]}
                  meta={sectionMeta(s, overview)}
                  onClick={() => goTo(s)}
                />
              ))}
            </nav>
          </aside>

          <main
            className="flex-1 min-w-0"
            data-testid="agency-portal-main"
            // Reserving a min-height here keeps the shell stable while
            // the next section's data is in flight — without it the
            // content column collapses to its loader's intrinsic height
            // and the viewport jumps as each section settles. 600 px
            // covers the shortest realistic section (empty-state cards)
            // without ever cropping the longest. v5 polish 2026-10-11.
            style={{ minHeight: 600 }}
          >
            {/* ADR-0263 §7.5 as amended for security (2026-11-03) —
                Portal header counters. Rendered above the section
                title so they're visible on every section (not only
                Overview). Each row hides when the plan doesn't set
                the dimension. */}
            <PortalCounters C={C} overview={overview} />
            {/* v3 (2026-10-10) — one portal-wide header per section.
                The rail already carries the "AGENCY PORTAL" kicker +
                agency name; the content column only shows its own
                section title + a one-line description. Removing the
                second kicker fixes the "Agency Portal title there
                twice" founder complaint. */}
            <div style={{ marginBottom: 20 }} data-testid="agency-portal-section-header">
              <h1
                style={{
                  fontFamily: serifFont,
                  fontSize: 'clamp(28px, 4vw, 40px)',
                  lineHeight: 1,
                  color: C.text,
                  letterSpacing: -1,
                }}
              >
                {sectionLabel}.
              </h1>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.muted,
                  marginTop: 6,
                  maxWidth: 640,
                }}
              >
                {sectionDesc}
              </div>
            </div>
            <PortalShellProvider value={true}>{children}</PortalShellProvider>
          </main>
        </div>
      </div>
    </div>
  );
}

function RailItem({
  C,
  active,
  label,
  meta,
  onClick,
}: {
  C: ReturnType<typeof useC>;
  active: boolean;
  label: string;
  meta: string | null;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        height: 32,
        background: active ? C.surface2 : 'transparent',
        border: `1px solid ${active ? C.border : 'transparent'}`,
        borderLeft: `2px solid ${active ? C.lime : 'transparent'}`,
        borderRadius: 2,
        fontFamily: sansFont,
        fontSize: 13,
        color: active ? C.text : C.muted,
        fontWeight: active ? 500 : 400,
        cursor: 'pointer',
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {/* Reserve a fixed-width slot for the right-aligned meta count so
          late-arriving overview data (first paint has `overview === null`
          → `meta === null`) doesn't shift the rail text horizontally
          when the counts land a tick later. v5 polish 2026-10-11. */}
      <span
        data-testid="portal-rail-meta"
        aria-hidden={meta === null}
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.muted,
          letterSpacing: 0.5,
          minWidth: 48,
          textAlign: 'right',
          flexShrink: 0,
          visibility: meta === null ? 'hidden' : 'visible',
        }}
      >
        {meta ?? '0'}
      </span>
    </button>
  );
}

/**
 * Resolve the right-aligned meta label for each rail item from the
 * overview response. Null → the item shows just its label.
 */
function sectionMeta(
  section: PortalSection,
  overview: AgencyOverviewWire | null,
): string | null {
  if (overview === null) return null;
  switch (section) {
    case 'overview':
      return null;
    case 'clients':
      return String(overview.clients_linked);
    case 'reports':
      return String(overview.reports_scanned_30d);
    case 'leads':
      return String(overview.widget.leads_30d);
    case 'referral':
      return overview.referral.slug === null ? 'Not set' : String(overview.referral.clicks_30d);
    case 'widget':
      return overview.widget.state === 'live'
        ? 'Live'
        : overview.widget.state === 'needs_origin'
          ? 'No origins'
          : 'Not set up';
    case 'white-label':
      return overview.white_label.configured ? 'On' : 'Off';
  }
}

// ─── ADR-0263 §7.5 as amended for security (2026-11-03) counters ────

const AGENCIES_CONTACT_URL = 'https://sill.so/agencies';

function formatIntegerUS(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-US');
}

function counterState(used: number, cap: number): 'ok' | 'amber' | 'coral' {
  if (cap <= 0) return 'ok';
  const pct = used / cap;
  if (pct >= 1) return 'coral';
  if (pct >= 0.8) return 'amber';
  return 'ok';
}

function PortalCounters({
  C,
  overview,
}: {
  C: ReturnType<typeof useC>;
  overview: AgencyOverviewWire | null;
}): JSX.Element | null {
  if (overview === null) return null;
  const { client_sites, scans } = overview;
  if (client_sites === null && scans === null) return null;
  return (
    <div
      data-testid="agency-portal-counters"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 20,
        alignItems: 'stretch',
      }}
    >
      {client_sites !== null ? (
        <CounterTile
          C={C}
          label="Client sites"
          used={client_sites.used}
          cap={client_sites.cap}
          suffix={null}
          testid="agency-portal-counter-client-sites"
        />
      ) : null}
      {scans !== null ? (
        <CounterTile
          C={C}
          label="Scans"
          used={scans.used}
          cap={scans.cap}
          suffix="this month"
          testid="agency-portal-counter-scans"
        />
      ) : null}
    </div>
  );
}

function CounterTile({
  C,
  label,
  used,
  cap,
  suffix,
  testid,
}: {
  C: ReturnType<typeof useC>;
  label: string;
  used: number;
  cap: number;
  suffix: string | null;
  testid: string;
}): JSX.Element {
  const state = counterState(used, cap);
  const accent =
    state === 'coral' ? C.coralFg : state === 'amber' ? C.amberFg : C.text;
  return (
    <div
      data-testid={testid}
      data-counter-state={state}
      style={{
        flex: '1 1 220px',
        minWidth: 220,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            letterSpacing: 2,
            color: C.muted,
            textTransform: 'uppercase',
            marginBottom: 2,
          }}
        >
          {label}
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 14, color: accent }}>
          <span style={{ fontWeight: 500 }}>{formatIntegerUS(used)}</span>
          <span style={{ color: C.muted }}>{' of '}</span>
          <span>{formatIntegerUS(cap)}</span>
          {suffix === null ? null : (
            <span style={{ color: C.muted }}>{' '}{suffix}</span>
          )}
        </div>
      </div>
      {state === 'ok' ? null : (
        <a
          href={AGENCIES_CONTACT_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.limeFg,
            textDecoration: 'underline',
            whiteSpace: 'nowrap',
          }}
        >
          Talk to us
        </a>
      )}
    </div>
  );
}

function ChipRow({
  section,
  goTo,
  C,
}: {
  section: PortalSection;
  goTo: (s: PortalSection) => void;
  C: ReturnType<typeof useC>;
}): JSX.Element {
  const items = useMemo(() => PORTAL_SECTIONS.slice(), []);
  return (
    <nav
      aria-label="Agency sections"
      data-testid="portal-chip-row"
      style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollSnapType: 'x proximity',
        paddingBottom: 8,
        marginBottom: 4,
      }}
    >
      {items.map((s) => {
        const active = s === section;
        return (
          <button
            key={s}
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => goTo(s)}
            style={{
              flex: '0 0 auto',
              scrollSnapAlign: 'start',
              height: 30,
              padding: '0 12px',
              background: active ? C.limeDim : 'transparent',
              border: `1px solid ${active ? C.border : C.borderSoft}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              color: active ? C.limeFg : C.muted,
              fontWeight: active ? 500 : 400,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            {SECTION_LABELS[s]}
          </button>
        );
      })}
    </nav>
  );
}
